import { useEffect, useState, useRef } from 'react';
import { Campaign } from '../config/campaigns';

interface StatsBarProps {
    readonly campaigns: readonly Campaign[];
    readonly totalRaisedBTC: number;
}

function AnimatedNumber({
    target,
    prefix = '',
    suffix = '',
    decimals,
}: {
    target: number;
    prefix?: string;
    suffix?: string;
    decimals?: number;
}) {
    const [value, setValue] = useState(0);
    const ref = useRef<HTMLSpanElement>(null);
    const hasBeenVisible = useRef(false);
    const prevTarget = useRef(0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    hasBeenVisible.current = true;
                    observer.disconnect();
                }
            },
            { threshold: 0.5 },
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    // Re-animate whenever target changes (and element has been visible)
    useEffect(() => {
        if (target === prevTarget.current) return;

        const from = prevTarget.current;
        prevTarget.current = target;

        // If not yet visible, just snap
        if (!hasBeenVisible.current) {
            setValue(target);
            return;
        }

        const duration = 1500;
        const start = performance.now();

        const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(from + (target - from) * eased);
            if (progress < 1) requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
    }, [target]);

    const display = decimals !== undefined
        ? value.toFixed(decimals)
        : Number.isInteger(target)
            ? Math.floor(value).toString()
            : value.toFixed(2);

    return (
        <span ref={ref} className="stats-bar__value">
            {prefix}
            {display}
            {suffix}
        </span>
    );
}

export function StatsBar({ campaigns, totalRaisedBTC }: StatsBarProps) {
    const onChainCampaigns = campaigns.filter((c) => !c.isExample);
    const totalBackers = onChainCampaigns.reduce((sum, c) => sum + c.backers, 0);
    const campaignCount = onChainCampaigns.length;

    return (
        <section id="stats" className="section">
            <div className="container">
                <div className="stats-bar">
                    <div className="stats-bar__item">
                        <AnimatedNumber target={totalRaisedBTC} suffix=" BTC" decimals={4} />
                        <span className="stats-bar__label">Total Raised</span>
                    </div>
                    <div className="stats-bar__item">
                        <AnimatedNumber target={campaignCount} />
                        <span className="stats-bar__label">Campaigns</span>
                    </div>
                    <div className="stats-bar__item">
                        <AnimatedNumber target={totalBackers} />
                        <span className="stats-bar__label">Unique Backers</span>
                    </div>
                    <div className="stats-bar__item">
                        <AnimatedNumber target={0} prefix="" suffix="" />
                        <span className="stats-bar__label">Platform Fees</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
